import { Blac, Cubit } from "blac-next";
import { z } from "zod";
import { ASSUMED_IDENTITY_EXCHANGE_ENDPOINT } from "src/api/ApiController";
import envVariables from "src/lib/envVariables";
import AuthenticationBloc from "src/state/UserCubit/AuthenticationBloc";
import { parseAssumedTokens } from "./AssumedIdentityCubit";

export type AssumeIdentityHandoverState =
  | { status: "idle" }
  | { status: "exchanging" }
  | { status: "failed"; reason: string };

const exchangeResponseSchema = z.object({
  data: z.object({ accessToken: z.string(), refreshToken: z.string() })
});

/** The code travels in the fragment so it never reaches a server or access log. */
export const readExchangeCode = (hash: string): string | undefined =>
  new URLSearchParams(hash.replace(/^#/, "")).get("code")?.trim() || undefined;

const exchangeFailureReason = (status: number): string => {
  if (status === 401) {
    return "The link has expired or its session has ended. Open the member app from the PMP again.";
  }
  if (status === 429) {
    return "Too many attempts. Wait a few minutes, then open the member app from the PMP again.";
  }
  return `The exchange failed (HTTP ${status}).`;
};

/**
 * Redeems the exchange code the PMP hands to a new tab for the member's tokens,
 * then starts the same read-only session as pasting the tokens.
 */
export default class AssumeIdentityHandoverCubit extends Cubit<AssumeIdentityHandoverState> {
  constructor() {
    super({ status: "idle" });
  }

  private readonly fail = (reason: string): void => {
    this.emit({ status: "failed", reason });
  };

  redeem = async (code: string | undefined): Promise<void> => {
    if (this.state.status !== "idle") {
      return;
    }
    if (!code) {
      this.fail("The link has no exchange code.");
      return;
    }
    this.emit({ status: "exchanging" });

    let response: Response;
    try {
      response = await fetch(
        `${envVariables.API_BASE_URL}${ASSUMED_IDENTITY_EXCHANGE_ENDPOINT}`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ exchangeCode: code })
        }
      );
    } catch {
      this.fail("Could not reach the server.");
      return;
    }
    if (!response.ok) {
      this.fail(exchangeFailureReason(response.status));
      return;
    }

    const body = exchangeResponseSchema.safeParse(
      await response.json().catch(() => undefined)
    );
    if (!body.success) {
      this.fail("The server returned an unexpected response.");
      return;
    }
    const tokens = body.data.data;
    const parsed = parseAssumedTokens(tokens);
    if (!parsed.ok) {
      this.fail(parsed.reason);
      return;
    }
    await Blac.getBloc(AuthenticationBloc).assumeIdentity(
      tokens,
      parsed.identity
    );
  };
}
